import React, { useState, useEffect, useCallback } from 'react';
import { RefreshCw, Search, ChevronDown, ChevronUp } from 'lucide-react';
import api from '../utils/api';

export default function UsersScreen() {
  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [searchQuery, setSearchQuery] = useState('');
  const [expandedUser, setExpandedUser] = useState(null);
  const [userAnalytics, setUserAnalytics] = useState(null);
  const [analyticsLoading, setAnalyticsLoading] = useState(false);

  const loadUsers = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const data = await api.getUsers();
      setUsers(data.users || data.data || data || []);
    } catch (err) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    loadUsers();
  }, [loadUsers]);

  const toggleUserDetails = async (userId) => {
    if (expandedUser === userId) {
      setExpandedUser(null);
      setUserAnalytics(null);
      return;
    }
    setExpandedUser(userId);
    setAnalyticsLoading(true);
    try {
      const data = await api.getUserAnalytics(userId);
      setUserAnalytics(data);
    } catch {
      setUserAnalytics(null);
    } finally {
      setAnalyticsLoading(false);
    }
  };

  const filteredUsers = users.filter(user => {
    if (!searchQuery) return true;
    const q = searchQuery.toLowerCase();
    return (
      String(user.userId || user.user_id || user.id || '').includes(q) ||
      String(user.username || '').toLowerCase().includes(q) ||
      String(user.firstName || user.first_name || '').toLowerCase().includes(q) ||
      String(user.phone || '').includes(q)
    );
  });

  return (
    <div>
      <div className="flex items-center justify-between mb-6">
        <h2 className="text-xl font-bold text-white">
          Пользователи
          {!loading && <span className="text-sm text-slate-400 font-normal ml-2">({users.length})</span>}
        </h2>
        <button
          onClick={loadUsers}
          disabled={loading}
          className="p-2 bg-slate-800 rounded-lg border border-slate-700 text-slate-400 hover:text-white disabled:opacity-50"
        >
          <RefreshCw size={16} className={loading ? 'animate-spin' : ''} />
        </button>
      </div>

      {/* Search */}
      <div className="relative mb-4">
        <Search size={14} className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-500" />
        <input
          type="text"
          placeholder="Поиск по ID, username, имени, телефону..."
          value={searchQuery}
          onChange={e => setSearchQuery(e.target.value)}
          className="w-full bg-slate-800 border border-slate-700 rounded-lg pl-9 pr-4 py-2 text-sm text-white placeholder-slate-500 focus:outline-none focus:border-blue-500"
        />
      </div>

      {error && (
        <div className="bg-red-500/10 border border-red-500/30 rounded-xl p-4 mb-4 text-red-400 text-sm">
          {error}
        </div>
      )}

      {/* Users list */}
      <div className="space-y-2">
        {loading ? (
          Array.from({ length: 5 }).map((_, i) => (
            <div key={i} className="bg-slate-800 rounded-xl border border-slate-700 p-4">
              <div className="h-5 bg-slate-700 rounded animate-pulse w-1/2" />
            </div>
          ))
        ) : filteredUsers.length === 0 ? (
          <div className="bg-slate-800 rounded-xl border border-slate-700 p-8 text-center text-slate-500">
            Пользователи не найдены
          </div>
        ) : (
          filteredUsers.map((user, i) => {
            const uid = user.userId || user.user_id || user.id;
            const isExpanded = expandedUser === uid;
            return (
              <div key={uid || i} className="bg-slate-800 rounded-xl border border-slate-700 overflow-hidden">
                <button
                  onClick={() => toggleUserDetails(uid)}
                  className="w-full flex items-center justify-between p-4 hover:bg-slate-700/30 transition-colors text-left"
                >
                  <div className="flex items-center gap-3 min-w-0">
                    <div className="w-8 h-8 rounded-full bg-slate-600 flex items-center justify-center text-white text-sm font-bold shrink-0">
                      {(user.firstName || user.first_name || user.username || 'U')[0]?.toUpperCase()}
                    </div>
                    <div className="min-w-0">
                      <p className="text-sm text-white font-medium truncate">
                        {user.firstName || user.first_name || ''} {user.lastName || user.last_name || ''}
                        {user.username && <span className="text-slate-400 ml-1">@{user.username}</span>}
                      </p>
                      <p className="text-xs text-slate-500">
                        ID: {uid}
                        {user.phone && ` · ${user.phone}`}
                      </p>
                    </div>
                  </div>
                  <div className="flex items-center gap-3 shrink-0">
                    {user.isVerified || user.is_verified ? (
                      <span className="px-2 py-0.5 rounded-full text-xs bg-green-500/20 text-green-400">KYC</span>
                    ) : (
                      <span className="px-2 py-0.5 rounded-full text-xs bg-slate-500/20 text-slate-400">Без KYC</span>
                    )}
                    <span className="text-xs text-slate-400">
                      {user.ordersCount ?? user.orders_count ?? '—'} ордеров
                    </span>
                    {isExpanded ? <ChevronUp size={14} className="text-slate-400" /> : <ChevronDown size={14} className="text-slate-400" />}
                  </div>
                </button>

                {isExpanded && (
                  <div className="border-t border-slate-700 p-4 bg-slate-800/50">
                    {analyticsLoading ? (
                      <div className="h-16 flex items-center justify-center">
                        <div className="w-5 h-5 border-2 border-blue-500 border-t-transparent rounded-full animate-spin" />
                      </div>
                    ) : userAnalytics ? (
                      <div className="grid grid-cols-2 md:grid-cols-4 gap-3 text-sm">
                        {Object.entries(userAnalytics).map(([key, value]) => {
                          if (typeof value === 'object' && value !== null) return null;
                          return (
                            <div key={key}>
                              <p className="text-xs text-slate-500">{key}</p>
                              <p className="text-white">{String(value)}</p>
                            </div>
                          );
                        })}
                      </div>
                    ) : (
                      <div className="grid grid-cols-2 md:grid-cols-4 gap-3 text-sm">
                        <div>
                          <p className="text-xs text-slate-500">Сессий</p>
                          <p className="text-white">{user.sessionsCount ?? user.sessions_count ?? '—'}</p>
                        </div>
                        <div>
                          <p className="text-xs text-slate-500">Ордера</p>
                          <p className="text-white">{user.ordersCount ?? user.orders_count ?? '—'}</p>
                        </div>
                        <div>
                          <p className="text-xs text-slate-500">Рефералы</p>
                          <p className="text-white">{user.referralsCount ?? user.referrals ?? '—'}</p>
                        </div>
                        <div>
                          <p className="text-xs text-slate-500">Регистрация</p>
                          <p className="text-white text-xs">{user.createdAt || user.created_at || '—'}</p>
                        </div>
                      </div>
                    )}
                  </div>
                )}
              </div>
            );
          })
        )}
      </div>
    </div>
  );
}
